import dynamic from 'next/dynamic';
import React, { useRef, useEffect, useState, useContext, useMemo } from 'react';
import { ListMagnifyingGlassIcon, XCircleIcon, FileCodeIcon, FileTxtIcon, FilePdfIcon, SpinnerGapIcon } from '@phosphor-icons/react';
import { Tooltip } from '@heroui/tooltip';
import { formatTimeIndex } from '@/shared/functions';
import { MediaObject } from '../../../shared/types';
import { extractPathAfterBulk, FetchWithVersionCheck } from '@/shared/functions';
import { AuthContext } from '@/app/contexts';
interface Source {
  sourceFilePath: string;
}
const speakerColors = ['#E69F00',
// Orange
'#56B4E9',
// Sky Blue
'#009E73',
// Green
'#F0E442',
// Yellow
'#0072B2',
// Deep Blue
'#D55E00',
// Vermilion
'#CC79A7',
// Pink/Purple
'#999999',
// Light Gray
'#882255',
// Dark Red/Purple
'#44AA99',
// Teal
'#117733',
// Forest Green
'#DDCC77' // Mustard Yellow
];
function getSpeakerColor(speakerIndex: number): string {
  return speakerColors[speakerIndex % speakerColors.length];
}
function padZero(str: string, len?: number) {
  const zeros = new Array(len || 2).join('0');
  return (zeros + str).slice(-(len || 2));
}
function invertColor(hex: string, bw: boolean = true): string {
  if (hex.indexOf('#') === 0) {
    hex = hex.slice(1);
  }
  if (hex.length === 3) {
    hex = hex.split('').map(x => x + x).join('');
  }
  if (hex.length !== 6) {
    throw new Error('Invalid HEX color.');
  }
  let r = parseInt(hex.slice(0, 2), 16),
    g = parseInt(hex.slice(2, 4), 16),
    b = parseInt(hex.slice(4, 6), 16);
  if (bw) {
    return r * 0.299 + g * 0.587 + b * 0.114 > 186 ? '#000000' : '#FFFFFF';
  }
  r = 255 - r;
  g = 255 - g;
  b = 255 - b;
  return `#${padZero(r.toString(16))}${padZero(g.toString(16))}${padZero(b.toString(16))}`;
}
interface TranscriptProps {
  mediaItem: MediaObject;
  transcriptRef: React.RefObject<HTMLDivElement>;
  transcript: any[];
  filteredTranscript: any[];
  activeSegmentIndex: number;
  handleSegmentClick: (startTime: number) => void;
  searchQuery: string;
  handleSearchChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  isSearchInputVisible: boolean;
  toggleSearchInputVisibility: () => void;
  searchInputRef: React.RefObject<HTMLInputElement>;
  transcriptSrc?: string;
  handleDownload: (source: Source) => void;
  position: 'side' | 'below';
  isPortrait: boolean;
  playerHeight: number;
  playerWidth: number;
  style?: React.CSSProperties;
  autoScroll?: boolean;
}
interface Speaker {
  bg: string;
  fg: string;
}
const calculateHeaderHeight = (text: string) => {
  const charsPerLine = 77;
  const lines = Math.ceil((text || '').length / charsPerLine);
  return Math.max(lines * 20, 20) + 10;
};
const calculateItemHeight = (text: string) => {
  const charsPerLine = 92;
  const lines = Math.ceil((text || '').length / charsPerLine);
  return Math.max(lines * 16, 26) + 6;
};

/** Innermost overflow scroll ancestor of `el` within `boundary` (does not walk past the document). */
function findInnermostScrollContainer(el: HTMLElement, boundary: HTMLElement): HTMLElement | null {
  let node: HTMLElement | null = el.parentElement;
  while (node && boundary.contains(node)) {
    const {
      overflowY
    } = getComputedStyle(node);
    const canScroll = (overflowY === 'auto' || overflowY === 'scroll' || overflowY === 'overlay') && node.scrollHeight > node.clientHeight;
    if (canScroll) return node;
    node = node.parentElement;
  }
  const {
    overflowY
  } = getComputedStyle(boundary);
  const boundaryScrolls = (overflowY === 'auto' || overflowY === 'scroll' || overflowY === 'overlay') && boundary.scrollHeight > boundary.clientHeight;
  return boundaryScrolls ? boundary : null;
}
const Transcript: React.FC<TranscriptProps> = ({
  mediaItem,
  transcriptRef,
  transcript,
  filteredTranscript,
  activeSegmentIndex,
  handleSegmentClick,
  searchQuery,
  handleSearchChange,
  isSearchInputVisible = true,
  toggleSearchInputVisibility,
  searchInputRef,
  transcriptSrc,
  handleDownload,
  position,
  isPortrait,
  playerHeight,
  playerWidth,
  style,
  autoScroll
}) => {
  const [speakers, setSpeakers] = useState<Record<string, Speaker> | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedSpeakers, setSelectedSpeakers] = useState<string[]>([]);
  const [transcriptHeight, setTranscriptHeight] = useState<number>(position === 'side' ? 400 : 285); // Initial height
  const [generatingPdf, setGeneratingPdf] = useState(false);
  const [displayedTranscript, setDisplayedTranscript] = useState<any[]>([]);
  const segmentRefs = useRef<(HTMLDivElement | null)[]>([]);
  const exportPdfPagesRef = useRef<HTMLDivElement>(null);
  const exportPdfLinkRef = useRef<HTMLAnchorElement>(null);
  const {
    idToken,
    accessToken
  } = useContext(AuthContext);
  useEffect(() => {
    const speakers: Record<string, Speaker> = {};
    for (const item of filteredTranscript) {
      if (!speakers[item.speaker]) {
        const colorCount = Object.keys(speakers).length + 1;
        const color = getSpeakerColor(colorCount);
        speakers[item.speaker] = {
          bg: color,
          fg: invertColor(color, true) // true for black/white text
        };
      }
    }
    if (Object.keys(speakers).length > 0) {
      setSpeakers(speakers);
    }
  }, [filteredTranscript]);
  useEffect(() => {
    const displayedTranscript = selectedSpeakers && selectedSpeakers.length > 0 ? filteredTranscript.filter(item => selectedSpeakers.includes(item.speaker)) : filteredTranscript;
    setDisplayedTranscript(displayedTranscript);
  }, [selectedSpeakers.length, filteredTranscript]);
  const activeDisplayIndex = useMemo(() => {
    if (activeSegmentIndex < 0) return -1;
    const active = filteredTranscript[activeSegmentIndex];
    if (!active) return -1;
    return displayedTranscript.findIndex(item => item.start === active.start && item.end === active.end);
  }, [activeSegmentIndex, filteredTranscript, displayedTranscript]);
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.ctrlKey && event.key === 'Backspace') {
        handleSearchChange({
          target: {
            value: ''
          }
        } as React.ChangeEvent<HTMLInputElement>);
        searchInputRef.current?.focus();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleSearchChange, searchInputRef]);
  useEffect(() => {
    // Simulate loading
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 1000); // Adjust the timeout as needed

    return () => clearTimeout(timer);
  }, []);
  useEffect(() => {
    if (!autoScroll || activeDisplayIndex < 0) return;
    const el = segmentRefs.current[activeDisplayIndex];
    const boundary = transcriptRef.current;
    if (!el || !boundary || !boundary.contains(el)) return;
    const scrollContainer = findInnermostScrollContainer(el, boundary);
    if (!scrollContainer) return;
    const elRect = el.getBoundingClientRect();
    const containerRect = scrollContainer.getBoundingClientRect();
    const isVisible = elRect.top >= containerRect.top && elRect.bottom <= containerRect.bottom;
    if (!isVisible) {
      const deltaY = elRect.top - containerRect.top - scrollContainer.clientHeight / 2 + elRect.height / 2;
      scrollContainer.scrollBy({
        top: deltaY,
        behavior: 'smooth'
      });
    }
  }, [activeDisplayIndex, autoScroll, transcriptRef]);
  useEffect(() => {
    const handleScroll = (event: Event) => {
      event.stopPropagation();
    };
    const transcriptElement = transcriptRef.current;
    transcriptElement?.addEventListener('scroll', handleScroll);
    return () => {
      transcriptElement?.removeEventListener('scroll', handleScroll);
    };
  }, [transcriptRef]);
  const handleResizePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    window.getSelection()?.removeAllRanges();
    const target = e.currentTarget;
    target.setPointerCapture(e.pointerId);
    const startY = e.clientY;
    const startHeight = transcriptHeight;
    let moveCount = 0;
    const onPointerMove = (moveEvent: PointerEvent) => {
      const newHeight = Math.max(200, startHeight + (moveEvent.clientY - startY));
      if (transcriptRef.current) {
        transcriptRef.current.style.height = `${newHeight}px`;
      }
      moveCount++;
    };
    const onPointerUp = (upEvent: PointerEvent) => {
      const finalHeight = Math.max(200, startHeight + (upEvent.clientY - startY));
      target.releasePointerCapture(upEvent.pointerId);
      setTranscriptHeight(finalHeight);
      target.removeEventListener('pointermove', onPointerMove);
      target.removeEventListener('pointerup', onPointerUp);
    };
    target.addEventListener('pointermove', onPointerMove);
    target.addEventListener('pointerup', onPointerUp);
  };
  const generatePdfOnServer = async () => {
    setGeneratingPdf(true);
    try {
      const transcriptFilename = extractPathAfterBulk(mediaItem.s3Url || mediaItem.mediaUrl || '/', true).truncatedFilePath;
      const downloadFilename = `${transcriptFilename && transcriptFilename !== '' ? `${transcriptFilename}.` : ''}transcript.pdf`;
      const body = {
        documentKind: 'transcript',
        documentData: {
          transcriptFilename: transcriptFilename,
          transcriptData: displayedTranscript
        }
      };
      const exportPdfPath = '/api/protected/exportPdf';
      let rawResult = await FetchWithVersionCheck(exportPdfPath, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`
        },
        // if wanting to scope, populate files/scopes/excludedFiles/excludedScopes
        body: JSON.stringify(body)
      });
      const result = await rawResult.arrayBuffer();
      let pdfUrl = '';
      if (result) {
        // use application/download to force a download
        const pdfBlob = new Blob([result], {
          type: 'application/pdf'
        });
        pdfUrl = URL.createObjectURL(pdfBlob);
      }
      exportPdfLinkRef.current!.download = downloadFilename;
      exportPdfLinkRef.current!.href = pdfUrl;
      exportPdfLinkRef.current!.click();
    } catch (error) {
      // toast error
    }
    setGeneratingPdf(false);
  };
  const getTranscript = async (transcriptSrc: string) => {
    if (!transcriptSrc || transcriptSrc.trim() === '') return;
    try {
      let response = null;
      let apiUrl = `/api/proxy/${encodeURIComponent(transcriptSrc)}`;
      response = await fetch(apiUrl, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`
        },
        method: 'GET'
      });
      return response;
    } catch (error) {
      console.error('Error loading transcript:', error);
      return undefined;
    }
  };
  const handleDownloadTranscriptSource = async () => {
    const mediaFilename = (mediaItem.s3Url || mediaItem.mediaUrl || '/').split('/').pop();
    const transcriptFilename = `${mediaFilename && mediaFilename !== '' ? `${mediaFilename}.` : ''}transcript.json`;
    if (!transcriptSrc || transcriptSrc.trim() === '') return;
    const transcriptResponse = await getTranscript(transcriptSrc);
    if (!transcriptResponse) return;
    const transcript = await transcriptResponse.json();
    const blob = new Blob([JSON.stringify(transcript)], {
      type: 'application/json'
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = transcriptFilename;
    a.click();
    URL.revokeObjectURL(url);
  };
  const handleDownloadAsText = async () => {
    const mediaFilename = (mediaItem.s3Url || mediaItem.mediaUrl || '/').split('/').pop();
    const transcriptFilename = `${mediaFilename && mediaFilename !== '' ? `${mediaFilename}.` : ''}transcript.txt`;
    const fileDisplayName = extractPathAfterBulk(mediaItem.s3Url || mediaItem.mediaUrl || '/').truncatedFilePath;
    let text = mediaFilename ? `Transcript for ${fileDisplayName}\n\n` : '';
    for (const item of transcript) {
      text += `${item.speaker} (${formatTimeIndex(item.start.toString())}): ${item.text}\n`;
    }
    const blob = new Blob([text], {
      type: 'text/plain'
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = transcriptFilename;
    a.click();
    URL.revokeObjectURL(url);
  };
  return <div className={`box-border rounded-lg p-4 w-full min-w-[245px] min-h-[200px] relative custom-scrollbar2
        z-100 overflow-y-auto border-1 border-b-3 border-gray-700 border-solid ${position === 'side' ? 'max-w-[500px]' : 'w-full'}`} ref={transcriptRef} style={{
    height: `${transcriptHeight}px`,
    // Set initial height
    backgroundColor: 'rgba(0, 0, 0, .5)',
    display: 'flex',
    flexDirection: 'column',
    resize: 'none',
    ...style
  }} data-sentry-component="Transcript" data-sentry-source-file="transcript.tsx">
      <div className='export-transcript hidden overflow-scroll'>
        <div ref={exportPdfPagesRef} className='flex flex-col gap-1'>
        </div>
      </div>
      <div className="box-border flex flex-col mb-1 gap-1 max-h-[80vh] overflow-y">
        <div className="flex items-center gap-2 flex-wrap">
          <label className="text-base font-light tracking-wider m-0 p-0 flex items-center gap-1 text-white whitespace-nowrap flex-shrink-0">
            Transcript
          </label>
          <div className="flex items-center gap-1 flex-shrink-0 ml-auto">
            {transcriptSrc && <Tooltip content="Download JSON file" placement="top" delay={700} closeDelay={0} classNames={{
            content: 'bg-black rounded-md border-0 border-transparent drop-shadow-none inner-shadow-none shadow-none px-1.5 py-1'
          }}>
                <button onClick={() => handleDownloadTranscriptSource()} className="flex items-center cursor-pointer hover:bg-white/20 rounded-sm p-1 transition-all duration-300 gap-1">
                  <FileCodeIcon size={20} />
                </button>
              </Tooltip>}
            {transcriptSrc && transcript.length > 0 && <Tooltip content="Export Text file" placement="top" delay={700} closeDelay={0} classNames={{
            content: 'bg-black rounded-md border-0 border-transparent drop-shadow-none inner-shadow-none shadow-none px-1.5 py-1'
          }}>
                <button onClick={() => {
              handleDownloadAsText();
            }} className="flex items-center cursor-pointer hover:bg-white/20 rounded-sm p-1 transition-all duration-300 gap-1">
                  <FileTxtIcon size={20} />
                </button>
              </Tooltip>}
            {transcriptSrc && filteredTranscript.length > 0 && <Tooltip content="Export PDF file" placement="top" delay={700} closeDelay={0} classNames={{
            content: 'bg-black rounded-md border-0 border-transparent drop-shadow-none inner-shadow-none shadow-none px-1.5 py-1'
          }}>
                <div>
                  <button onClick={() => generatePdfOnServer()} disabled={generatingPdf} className="flex items-center cursor-pointer hover:bg-white/20 rounded-sm p-1 transition-all
                      duration-300 gap-1 disabled:opacity-50 disabled:cursor-not-allowed">
                    {generatingPdf ? <SpinnerGapIcon size={20} className='animate-spin-slow' /> : <FilePdfIcon size={20} />}
                  </button>
                  <a className='opacity-0 h-[1px] w-[1px] overflow-hidden' ref={exportPdfLinkRef} href={""} download="transcript.pdf"></a>
                </div>
              </Tooltip>}
          </div>
        </div>
        <div className="box-border flex flex-row items-center rounded-md cursor-pointer border-1 border-white/20 border-solid transition-all duration-300 gap-1 w-full pl-1 py-0.5">
          <ListMagnifyingGlassIcon className="w-5 h-5 flex-shrink-0" data-sentry-element="ListMagnifyingGlassIcon" data-sentry-source-file="transcript.tsx" />
          <input ref={searchInputRef} type="text" value={searchQuery} onChange={handleSearchChange} placeholder={searchQuery ? '' : ''} className="box-border flex flex-grow w-[40px] outline-none placeholder-gray-300 text-gray-300 bg-white/0 border-none tracking-wide font-lighter overflow-hidden" />
          {searchQuery && <div className='flex items-center gap-1'>
              <XCircleIcon size={20} weight='fill' className='mr-1 text-white/80 hover:text-red-500 transition-all duration-1000 cursor-pointer' onClick={() => {
            handleSearchChange({
              target: {
                value: ''
              }
            } as React.ChangeEvent<HTMLInputElement>);
            searchInputRef.current?.focus();
          }} />
            </div>}
        </div>
      </div>
      {/* Speaker Key */}
      {speakers && <div className="flex flex-wrap items-center justify-start gap-2 mt-2 mb-3 border-1 border-white/5 border-solid rounded-sm px-2 py-1 bg-white/5">
        <label>Speakers:</label>
        {Object.keys(speakers || {}).map((speaker, index) => {
        const speakerStyle = speakers?.[speaker]?.bg || 'gray';
        const speakerLabel = speaker === "undefined" ? "Unidentified" : speaker;
        return <div key={speaker} className={`px-1 py-0.5 rounded-sm cursor-pointer ${selectedSpeakers.length > 0 && selectedSpeakers.includes(speaker) ? 'border-2 border-white' : ''}`} style={{
          backgroundColor: speakerStyle,
          opacity: selectedSpeakers.length === 0 || selectedSpeakers.includes(speaker) ? 1 : 0.5
        }} onClick={() => {
          if (Object.keys(speakers).length > 1) {
            setSelectedSpeakers(selectedSpeakers.includes(speaker) ? selectedSpeakers.filter((speak: string) => speak !== speaker) : [...selectedSpeakers, speaker]);
          }
        }}>
              <div className="font-medium tracking-wider" style={{
            color: invertColor(speakerStyle)
          }}>
                {speakerLabel}
              </div>
            </div>;
      })}
      </div>}
      {/* Transcript Content */}
      <div className="flex-grow pt-3 pr-4 space-y-2 text-sm text-left overflow-y-auto transcript-container custom-scrollbar2">
        {displayedTranscript.length > 0 ? displayedTranscript.map((item, index) => <div key={index} ref={el => {
        segmentRefs.current[index] = el;
        return undefined;
      }} onClick={() => handleSegmentClick(item.start)} className={`flex items-start p-2 rounded-md transition-colors cursor-pointer relative z-0 mt-[-10px] ${index === activeDisplayIndex ? 'bg-white/20 text-white border-1 border-white/20 border-solid' : 'hover:bg-gray-700 text-gray-300 border-1 border-white/0 border-solid'}`}>
              {speakers && <Tooltip content={<div className="px-1 py-0.5 rounded-sm font-medium" style={{
          backgroundColor: speakers?.[item.speaker]?.bg || 'black',
          color: speakers?.[item.speaker]?.fg || 'white',
          visibility: Object.keys(speakers).length > 1 ? 'visible' : 'hidden'
        }}>
                      {item.speaker === "undefined" ? "Unidentified" : item.speaker}
                    </div>} offset={12} delay={300} closeDelay={0} placement={'left'} classNames={{
          content: 'bg-transparent rounded-md border-0 border-transparent drop-shadow-none inner-shadow-none shadow-none p-0 m-0'
        }}>
                  <div className={`flex items-center w-fit font-medium tracking-wider mr-2 rounded-sm px-1 py-0.5`} style={{
            backgroundColor: speakers[item.speaker]?.bg || 'black',
            color: speakers[item.speaker]?.fg || 'white'
          }}>
                    {formatTimeIndex(item.start.toString())}
                  </div>
                </Tooltip>}
              <div className="mt-[2px] text-white font-extralight tracking-wide">
                {item.text}
              </div>
            </div>) : <div className="flex items-center justify-center text-white text-center w-full font-semibold tracking-wider">
            {isLoading ? <div className='flex items-center gap-2'><SpinnerGapIcon size={20} className='animate-spin-slow animate-pulse' /> <label className='text-white/80'>Transcripts loading...</label></div> : searchQuery ? "No results found." : ""}
          </div>}
      </div>
      {/* Resize Handle */}
      <div className='resize-ns'>
        <div onPointerDown={handleResizePointerDown} style={{
        touchAction: 'none'
      }}>
          <span></span>
          <span></span>
          <span></span>
        </div>
      </div>
    </div>;
};
export default Transcript;